import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { NestedCheckboxTree } from 'ui-components/components/nested-checkbox-tree';

import messages from './messages';
import styles from './styles.module.less';
import { Providers } from '../../../../../../enums/providers';
import { SellerSettings } from '../../../../../../enums/seller-settings/seller-settings';
import { useSellerSetting } from '../../../../../../hooks/use-seller-setting';
import {
  CarrierSharingActiveServicesPropType,
  CarrierSharingCarriersPropType,
  CarrierSharingDataPropType,
  CarrierSharingLabelProvidersPropType,
} from '../../../types';

const excludedSharingIntegrations = [
  Providers.ClientStamps.labelProviderId,
  Providers.ClientFedex.labelProviderId,
  Providers.ClientUPS.labelProviderId,
  Providers.ClientDhlExpress.labelProviderId,
  Providers.ClientSharedCarriers.labelProviderId,
];

const carrierNodeIdentifier = (service, suffix = undefined) => {
  const identifier = `${service.carrierId}_${service.labelProviderId}_${service.sellerLabelProviderId}`;

  if (suffix) {
    return `${identifier}_${suffix}`;
  }

  return identifier;
};

const serviceNodeIdentifier = (service) => {
  return `${service.carrierId}_${service.sellerLabelProviderId}_${service.serviceId}`;
};

const mapService = (carrier, service, data, destinationValue, destinationLabel) => {
  if (!carrier.children) {
    // oxlint-disable-next-line no-param-reassign
    carrier.children = [];
  }

  let services = carrier.children.find((child) => child.value === carrierNodeIdentifier(service, destinationValue));

  if (!services) {
    services = {
      value: carrierNodeIdentifier(service, destinationValue),
      label: destinationLabel,
      children: [],
    };
    carrier.children.push(services);
  }

  const foundChecked = data?.selectedServices?.find(
    (selectedService) =>
      parseInt(service.serviceId, 10) === selectedService.service &&
      parseInt(service.sellerLabelProviderId, 10) === selectedService.sellerLabelProviderId,
  );

  if (foundChecked !== undefined) {
    // adding this fo validation purposes
    foundChecked.carrierId = service.carrierId;
  }

  services.children.push({
    value: serviceNodeIdentifier(service),
    label: service.name,
    checked: foundChecked !== undefined,
  });
};

const isUPSbyShipstation = (providerId) => {
  return providerId === Providers.UPSWallet.labelProviderId;
};

export const ServicesTreeDataWrapper = ({
  carrierSharingData,
  setCarrierSharingData,
  carriers,
  labelProviders,
  activeServices,
  disabled,
  setDataAvailable,
}) => {
  const intl = useIntl();
  const canShareUPSAuctaneRatesWithProviders =
    useSellerSetting(SellerSettings.canShareUPSAuctaneRatesWithProviders)?.toLowerCase() === 'true';

  const requireToggleDisabled = useMemo(() => {
    return disabled || carrierSharingData.selectedServices.length === 0;
  }, [carrierSharingData.selectedServices.length, disabled]);

  const createNodes = useCallback(() => {
    const nodesTmp = labelProviders.flatMap((labelProvider) => {
      if (isUPSbyShipstation(labelProvider.labelProviderId) && !canShareUPSAuctaneRatesWithProviders) {
        return [];
      }

      if (excludedSharingIntegrations.includes(labelProvider.labelProviderId)) {
        return [];
      }

      const carrierItem = carriers.find((item) => item.labelProviderId === labelProvider.labelProviderId);
      const servicesAvailable = activeServices.some((item) => item.labelProviderId === labelProvider.labelProviderId);

      if (!servicesAvailable) {
        return [];
      }

      if (carrierItem) {
        return {
          extendable: true,
          value: carrierNodeIdentifier({
            ...labelProvider,
            carrierId: carrierItem.carrierId,
          }),
          label: labelProvider.name !== null ? `${carrierItem.name} (${labelProvider.name})` : carrierItem.name,
        };
      }

      return [];
    });

    activeServices.forEach((service) => {
      const carrier = nodesTmp.find((node) => node.value === carrierNodeIdentifier(service));

      if (carrier) {
        if (service.features.supportsInternationalShipping) {
          mapService(carrier, service, carrierSharingData, 'international', intl.formatMessage(messages.internationalLabel));
        } else {
          mapService(carrier, service, carrierSharingData, 'domestic', intl.formatMessage(messages.domesticLabel));
        }
      }
    });

    return nodesTmp;
  }, [labelProviders, activeServices, canShareUPSAuctaneRatesWithProviders, carriers, carrierSharingData, intl]);

  const requireSharedServiceChanged = useCallback(() => {
    setCarrierSharingData((_carrierSharingData) => {
      return {
        ..._carrierSharingData,
        isRequired: !_carrierSharingData.isRequired,
      };
    });
  }, [setCarrierSharingData]);

  // this value should be calculated only once because it is only for component setup
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const nodes = useMemo(() => createNodes(), []);

  // setDataAvailable should be memoized if u want to use it here
  // in current configuration setDataAvailable is a function that comes from useState so it is already "memoized"
  useEffect(() => {
    setDataAvailable(nodes.length > 0);
  }, [nodes, setDataAvailable]);

  const onSelectedServicesChanged = useCallback(
    (newChecked) => {
      setCarrierSharingData({
        ...carrierSharingData,
        selectedServices: newChecked.map((c) => {
          const split = c.split('_');
          return {
            carrierId: parseInt(split[0], 10),
            sellerLabelProviderId: parseInt(split[1], 10),
            service: parseInt(split[2], 10),
          };
        }),
      });
    },
    [carrierSharingData, setCarrierSharingData],
  );

  return (
    <>
      {nodes.length === 0 ? (
        <p className={styles['no-carriers']}>
          <FormattedMessage
            {...messages.noCarriers}
            values={{
              link: (chunks) => <Link to='/settings/carriers'>{chunks}</Link>,
            }}
          />
        </p>
      ) : (
        <>
          <div className={styles.require}>
            <CheckboxSlider
              checkboxId='require-shared-service-toggle'
              checked={carrierSharingData.isRequired}
              className={styles['checkbox-slider']}
              disabled={requireToggleDisabled}
              onChange={requireSharedServiceChanged}
            />
            <span className={styles['slider-description']} onClick={requireSharedServiceChanged}>
              <FormattedMessage {...messages.requiredDescription} />
            </span>
          </div>
          <NestedCheckboxTree
            addRootNode
            checkedCallback={onSelectedServicesChanged}
            data={nodes}
            disabled={disabled}
            title={intl.formatMessage(messages.treeTitle)}
          />
        </>
      )}
    </>
  );
};

ServicesTreeDataWrapper.propTypes = {
  carrierSharingData: CarrierSharingDataPropType.isRequired,
  setCarrierSharingData: PropTypes.func.isRequired,
  carriers: PropTypes.arrayOf(CarrierSharingCarriersPropType).isRequired,
  labelProviders: PropTypes.arrayOf(CarrierSharingLabelProvidersPropType).isRequired,
  activeServices: PropTypes.arrayOf(CarrierSharingActiveServicesPropType).isRequired,
  disabled: PropTypes.bool.isRequired,
  setDataAvailable: PropTypes.func.isRequired,
};
